import { apiClient, ensureCsrfCookie } from '../../api/client';

export type BusinessUnitType = 'network' | 'filial';

export interface BusinessUnit {
  id: number;
  account_id: number;
  parent_id: number | null;
  type: BusinessUnitType;
  name: string;
  slug: string;
  address: string | null;
  legal_entity: string | null;
  legal_inn: string | null;
  default_domain: string | null;
  status: 'active' | 'archived';
  meta: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  parent?: BusinessUnit | null;
  children?: BusinessUnit[];
}

export interface Service {
  id: number;
  account_id: number;
  filial_id: number;
  name: string;
  slug: string;
  description: string | null;
  base_price_kopeks: number;
  currency: string;
  duration_minutes: number | null;
  status: 'active' | 'archived';
  meta: Record<string, unknown> | null;
  filial?: BusinessUnit;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Customer {
  id: number;
  account_id: number;
  primary_filial_id: number | null;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  birth_date: string | null;
  gender: 'male' | 'female' | 'unknown';
  phone_normalized: string | null;
  phone_display: string | null;
  email: string | null;
  meta: Record<string, unknown> | null;
  notes: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Tag {
  id: number;
  account_id: number;
  name: string;
  slug: string;
  color: string | null;
  category: string | null;
}

export async function fetchBusinessUnits(
  filters: { type?: BusinessUnitType; withRelations?: string } = {}
): Promise<BusinessUnit[]> {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.withRelations) params.set('with', filters.withRelations);
  const { data } = await apiClient.get(`/business-units?${params.toString()}`);
  return data.data ?? data;
}

export async function fetchServices(filialId?: number): Promise<Service[]> {
  const params = filialId ? `?filial_id=${filialId}` : '';
  const { data } = await apiClient.get(`/services${params}`);
  return data.data ?? data;
}

export async function fetchTags(category?: string): Promise<Tag[]> {
  const params = category ? `?category=${encodeURIComponent(category)}` : '';
  const { data } = await apiClient.get(`/tags${params}`);
  return data.data ?? data;
}

export interface CreateCustomerPayload {
  phone_display: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  filial_id?: number;
  acquisition_source?: string;
}

export async function createCustomer(
  payload: CreateCustomerPayload
): Promise<{ data: Customer; existed: boolean }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post('/customers', payload);
  return { data: data.data, existed: Boolean(data.existed) };
}

/**
 * Сохранить выбор филиала за человеком (`s320`, Д3/Д47).
 *
 * ⛔ Выбор переключателя жил только в `localStorage` и терялся на первом же
 * проходе по меню: сервер о нём не знал и подставлял свой филиал — экран
 * показывал чужой филиал под подписью выбранного. Теперь выбор хранится за
 * человеком, и сервер фильтрует по нему все экраны участка.
 */
export async function sohranitTekushchiyFilial(filialId: number | null): Promise<void> {
  await apiClient.patch('/profile/tekushchiy-filial', { filial_id: filialId });
}
